/**
 * Page de gestion des organisations de l'utilisateur
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/app/providers';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Plus,
  Settings,
  Users,
  Crown,
  Shield,
  User,
  Search,
  Loader2,
  Globe,
  Bug
} from 'lucide-react';
import * as Sentry from '@sentry/react';

import { useUserAffiliation } from '@/shared/hooks/useUserAffiliation';
import { OrgaConfigurationRepository } from '@/services/firebase/orga-configuration-repository';
import { getOrgaCompletionStatus } from '@/shared/hooks/useOrgaCompletion';
import SeoTags from '@/components/shared/SeoTags';
import { Button, Card, CardBody, Badge } from '@/components/ui';
import ImpersonationPanel from '@/components/admin/ImpersonationPanel';

interface OrganizationWithInfo {
  id: string;
  organizationId: string;
  role: string;
  status: string;
  joinedAt?: any; // Peut être Date, string ISO ou Timestamp Firestore
  /** Date de dernière mise à jour de la configuration de l'organisation */
  lastUpdatedAt?: any;
  organizationInfo?: {
    name: string;
    logo?: string;
  };
  configStatus?: {
    isActive: boolean;
    isStripeReady: boolean;
    hasShippingConfigured: boolean;
    hasSenderAddress: boolean;
    hasLegalInfo: boolean;
    isGeneralReady: boolean;
    hasContactConfigured: boolean;
    isPortalReady: boolean;
    activeMode?: string;
  };
}

const ManageOrganizationsPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    currentUser,
    isAdmin: isSuperAdmin,
    customer,
    customerLoading,
    permissionsInitialized,
  } = useAuth();
  const customerId = customer?.id || null;
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [organizationsWithInfo, setOrganizationsWithInfo] = useState<OrganizationWithInfo[]>([]);
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);
  const [viewMode, setViewMode] = useState<'my' | 'all'>('my'); // 'my' = mes organisations, 'all' = toutes les organisations

  // Utiliser le système d'affiliation qui fonctionne
  const { affiliations, loading: isLoading, getUserOrganizations } = useUserAffiliation();

  const superAdminDefaultApplied = useRef(false);
  const loadingUserData = customerLoading || !permissionsInitialized;

  // Super admin : onglet "Toutes les organisations" par défaut (évite liste vide au 1er chargement)
  useEffect(() => {
    if (isSuperAdmin && !superAdminDefaultApplied.current) {
      setViewMode('all');
      superAdminDefaultApplied.current = true;
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    console.log('🔍 ManageOrganizationsPage - isSuperAdmin:', isSuperAdmin, 'loading:', loadingUserData);
  }, [isSuperAdmin, loadingUserData]);

  // Debug: Log pour vérifier les affiliations
  useEffect(() => {
    console.log('🔍 ManageOrganizationsPage - Debug:', {
      customerId,
      customerLoading,
      hasCurrentUser: !!currentUser,
      currentUserUid: currentUser?.uid,
      affiliationsCount: affiliations.length,
      affiliations: affiliations,
      isLoading
    });
  }, [customerId, customerLoading, currentUser, affiliations, isLoading]);

  useEffect(() => {
    if (customerId && !customerLoading && currentUser) {
      console.log('🔄 ManageOrganizationsPage - Calling getUserOrganizations for customerId:', customerId);
      getUserOrganizations();
    }
  }, [customerId, customerLoading, currentUser]); // Ne pas inclure getUserOrganizations pour éviter les boucles


  // Charger les informations détaillées des organisations
  useEffect(() => {
    const loadOrganizationsInfo = async () => {
      if (isLoading || loadingUserData) {
        setIsLoadingComplete(false);
        return;
      }

      try {
        let orgsWithInfo: OrganizationWithInfo[] = [];

        // Mode "Toutes les organisations" (super admin uniquement)
        if (viewMode === 'all' && isSuperAdmin) {
          const allConfigs = await OrgaConfigurationRepository.getAll();

          orgsWithInfo = allConfigs.map((config) => {
            const status = getOrgaCompletionStatus(config);
            const updatedAt = config.updatedAt instanceof Date ? config.updatedAt : new Date();

            return {
              id: config.id || config.organizationId,
              organizationId: config.organizationId,
              role: 'owner', // Super admin = owner sur tout
              status: 'active',
              lastUpdatedAt: updatedAt,
              organizationInfo: {
                name: config.name,
                logo: config.personalization?.logo || '',
              },
              configStatus: {
                isActive: config.isActive || false,
                isStripeReady: status.isStripeValid,
                hasShippingConfigured: status.hasShippingMethods,
                hasSenderAddress: status.hasSenderAddress,
                hasLegalInfo: status.hasLegalInfo,
                isGeneralReady: status.isGeneralReady,
                hasContactConfigured: status.hasContactConfigured,
                isPortalReady: status.isPortalReady,
                activeMode: status.activeMode,
              }
            };
          });
        }
        // Mode "Mes organisations" (par défaut)
        else {
          if (!affiliations.length) {
            setOrganizationsWithInfo([]);
            setIsLoadingComplete(true);
            return;
          }

          orgsWithInfo = await Promise.all(
            affiliations.map(async (affiliation) => {
              try {
                const orgConfig = await OrgaConfigurationRepository.getByOrganizationId(affiliation.organizationId);

                const status = getOrgaCompletionStatus(orgConfig || null);

                const updatedAt = orgConfig?.updatedAt instanceof Date ? orgConfig.updatedAt : undefined;

                return {
                  ...affiliation,
                  role: affiliation.role || affiliation.roleName || 'member',
                  lastUpdatedAt: updatedAt,
                  organizationInfo: {
                    name: orgConfig?.name || affiliation.organizationId,
                    logo: orgConfig?.personalization?.logo || '',
                  },
                  configStatus: {
                    isActive: orgConfig?.isActive || false,
                    isStripeReady: status.isStripeValid,
                    hasShippingConfigured: status.hasShippingMethods,
                    hasSenderAddress: status.hasSenderAddress,
                    hasLegalInfo: status.hasLegalInfo,
                    isGeneralReady: status.isGeneralReady,
                    hasContactConfigured: status.hasContactConfigured,
                    isPortalReady: status.isPortalReady,
                    activeMode: status.activeMode,
                  }
                };
              } catch (error) {
                return {
                  ...affiliation,
                  role: affiliation.role || affiliation.roleName || 'member',
                  lastUpdatedAt: undefined,
                  organizationInfo: {
                    name: affiliation.organizationId,
                    logo: '',
                  },
                  configStatus: {
                    isActive: false,
                    isStripeReady: false,
                    hasShippingConfigured: false,
                    hasSenderAddress: false,
                    hasLegalInfo: false,
                    isGeneralReady: false,
                    hasContactConfigured: false,
                    isPortalReady: false,
                  }
                };
              }
            })
          );
        }

        setOrganizationsWithInfo(orgsWithInfo);
      } catch (error) {
        console.error('Erreur lors du chargement des espaces de vente:', error);
        toast.error('Erreur lors du chargement des espaces de vente');
      } finally {
        setIsLoadingComplete(true);
      }
    };

    loadOrganizationsInfo();
  }, [affiliations, isLoading, loadingUserData, viewMode, isSuperAdmin]);


  // Tous les utilisateurs connectés peuvent créer une organisation
  const canCreateOrg = !!currentUser;


  // Filtrer les organisations selon le terme de recherche
  const filteredOrganizations = organizationsWithInfo.filter(org =>
    org.organizationId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (org.organizationInfo?.name && org.organizationInfo.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCreateOrganization = () => {
    navigate('/organizations/create');
  };

  const handleOrganizationClick = (organizationId: string) => {
    // Rediriger vers la page de commande de l'organisation
    navigate(`/orga/${organizationId}/order`);
  };

  const handleAdminClick = (organizationId: string) => {
    // Rediriger vers la page d'administration de l'organisation
    navigate(`/orga/${organizationId}/admin`);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-4 h-4 text-warning" />;
      case 'admin':
        return <Shield className="w-4 h-4 text-primary" />;
      default:
        return <User className="w-4 h-4 text-neutral-600" />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return t('organizations.switcher.roles.owner');
      case 'admin':
        return t('organizations.switcher.roles.admin');
      case 'member':
        return t('organizations.switcher.roles.member');
      default:
        return role;
    }
  };

  const formatJoinedDate = (joinedAt: any): string => {
    if (joinedAt == null) return 'N/A';

    try {
      // Timestamp Firestore (objet avec toDate)
      if (joinedAt.toDate && typeof joinedAt.toDate === 'function') {
        return joinedAt.toDate().toLocaleDateString('fr-FR');
      }
      // Timestamp Firestore sérialisé (objet avec seconds)
      if (typeof joinedAt === 'object' && typeof joinedAt.seconds === 'number') {
        return new Date(joinedAt.seconds * 1000).toLocaleDateString('fr-FR');
      }
      // Date ou string ISO
      const date = joinedAt instanceof Date ? joinedAt : new Date(joinedAt);
      if (Number.isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('fr-FR');
    } catch (error) {
      return 'N/A';
    }
  };

  /** Retourne le libellé de la première étape de configuration manquante. */
  const getConfigIncompleteLabel = (org: OrganizationWithInfo): string => {
    const c = org.configStatus;
    if (!c) return t('organizations.manage.configStepGeneral');
    if (!c.isActive) return t('organizations.manage.configStepGeneral');
    if (!c.isGeneralReady) {
      return !c.hasLegalInfo ? t('organizations.manage.configStepLegal') : t('organizations.manage.configStepGeneral');
    }
    if (!c.isStripeReady) return t('organizations.manage.configStepPayment');
    if (!c.hasShippingConfigured) return t('organizations.manage.configStepShipping');
    if (!c.hasSenderAddress) return t('organizations.manage.configStepSender');
    if (!c.hasContactConfigured) return t('organizations.manage.configStepContacts');
    return t('organizations.manage.configStepGeneral');
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            {t('organizations.manage.unauthorized')}
          </h2>
          <p className="text-foreground-secondary mb-6">
            {t('organizations.manage.mustLogin')}
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate('/auth/login')}
          >
            {t('organizations.manage.signIn')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-8">
        <SeoTags
          title={`${t('header.organizations')} | CashLive`}
          description={t('organizations.manage.subtitleManage')}
          url={typeof window !== 'undefined' ? window.location.href : ''}
          noIndex={true}
        />
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold text-foreground">
                  {viewMode === 'all' && isSuperAdmin ? t('organizations.manage.tabs.all') : t('organizations.manage.title')}
                </h1>
                {isSuperAdmin && (
                  <Badge variant="info" size="md" className="border-0 bg-gradient-to-r from-secondary to-secondary-light flex-shrink-0 !gap-1.5">
                    <Crown className="w-3 h-3 flex-shrink-0" />
                    <span className="whitespace-nowrap leading-none">{t('organizations.manage.superAdmin')}</span>
                  </Badge>
                )}
              </div>
              <p className="text-foreground-secondary mt-2">
                {viewMode === 'all' && isSuperAdmin
                  ? t('organizations.manage.subtitleAll')
                  : t('organizations.manage.subtitleManage')
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isSuperAdmin && (
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  Sentry.captureMessage('Sentry test - Manage Orga (super admin)', 'info');
                  toast.success('Événement Sentry envoyé (vérifiez le dashboard Sentry)');
                }}
                leftIcon={<Bug className="w-4 h-4" />}
                title="Envoyer un événement test à Sentry"
              >
                Sentry test
              </Button>
            )}
            {canCreateOrg && (
              <Button
                variant="primary"
                size="md"
                onClick={handleCreateOrganization}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                {t('organizations.manage.newOrganization')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <ImpersonationPanel />

      {/* Barre de recherche */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-foreground-secondary w-4 h-4" />
        <input
          type="text"
          placeholder={t('organizations.manage.search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg focus:ring-2 focus:ring-primary-light focus:border-primary bg-surface text-foreground"
        />
      </div>

      {/* Filtres - Chips - Visible uniquement pour les super admins */}
      {isSuperAdmin && (
        <div className="mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-foreground-secondary">Affichage :</span>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setViewMode('my')}
                className={`
                    inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                    ${viewMode === 'my'
                    ? 'bg-primary text-white shadow-md hover:shadow-lg'
                    : 'bg-surface text-foreground-secondary hover:bg-background-tertiary hover:text-foreground border border-border'}
                  `}
              >
                <User className="w-4 h-4" />
                <span>{t('organizations.manage.tabs.my')}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${viewMode === 'my'
                  ? 'bg-white/20 text-white'
                  : 'bg-background-tertiary text-foreground-secondary'
                  }`}>
                  {affiliations.length}
                </span>
              </button>
              <button
                onClick={() => setViewMode('all')}
                className={`
                    inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                    ${viewMode === 'all'
                    ? 'bg-secondary text-white shadow-md hover:shadow-lg'
                    : 'bg-surface text-foreground-secondary hover:bg-background-tertiary hover:text-foreground border border-border'}
                  `}
              >
                <Globe className="w-4 h-4" />
                <span>{t('organizations.manage.tabs.all')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Liste des organisations avec loader */}
      {!isLoadingComplete ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
          <p className="text-foreground-secondary">{t('organizations.switcher.loading')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredOrganizations.length === 0 ? (
            <div className="col-span-full py-12">
              {searchTerm ? (
                <div className="text-center">
                  <Building2 className="w-16 h-16 text-foreground-secondary mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {t('organizations.manage.noResults')}
                  </h3>
                  <p className="text-foreground-secondary mb-6">
                    {t('organizations.manage.noResultsDesc')}
                  </p>
                </div>
              ) : (
                <div className="max-w-4xl mx-auto text-left space-y-6">
                  <Card variant="elevated" className="rounded-3xl">
                    <CardBody className="p-8">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-primary flex items-center justify-center text-white text-2xl cursor-pointer">
                          🚀
                        </div>
                        <div>
                          <p className="text-sm uppercase tracking-[0.2em] text-primary font-semibold">CashLive</p>
                          <h4 className="text-2xl font-bold text-foreground">Démarrez en quelques minutes</h4>
                        </div>
                      </div>
                      <p className="text-foreground-secondary">
                        CashLive est 100% clé-en-main : nous gérons les paiements, les commandes et les livraisons.<br />
                        <span className="font-semibold text-foreground">Vous, vous vendez.</span>
                      </p>
                    </CardBody>
                  </Card>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h5 className="text-lg font-semibold text-foreground">Comment démarrer ?</h5>
                      <Badge variant="info" size="sm">
                        4 étapes
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        {
                          title: 'Créer votre espace de vente',
                          desc: 'Votre espace de vente : friperie, boutique, live TikTok, collectif… Cliquez sur « Créer un espace de vente » ci-dessous.'
                        },
                        {
                          title: 'Activer une méthode de paiement',
                          desc: 'Configurez votre mode de paiement (Stripe Connect, Stripe API key ou PayPal selon votre configuration) pour encaisser et recevoir vos virements.'
                        },
                        {
                          title: 'Configurer la livraison',
                          desc: 'Ajoutez vos méthodes de livraison : Sendcloud avec vos clés API et/ou Mondial Relay direct. Les frais sont liés à vos propres contrats.'
                        },
                        {
                          title: 'Commencer à vendre',
                          desc: 'Partagez le lien de votre espace de vente pendant vos lives : CashLive gère paiements, commandes, adresses et étiquettes.'
                        }
                      ].map((step) => (
                        <Card key={step.title} variant="default" className="rounded-2xl">
                          <CardBody className="p-5">
                            <p className="font-semibold text-foreground mb-2">{step.title}</p>
                            <p className="text-sm text-foreground-secondary">{step.desc}</p>
                          </CardBody>
                        </Card>
                      ))}
                    </div>
                  </div>

                  <Card variant="outlined" className="rounded-3xl border-primary-20 bg-primary-5">
                    <CardBody className="p-5">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">💡</div>
                        <p className="text-primary-dark text-sm font-medium">
                          Après la création, rendez-vous dans <strong>Admin &gt; Configuration</strong>, puis ouvrez les sections
                          <strong> Paiement</strong> et <strong> Livraison</strong> pour finaliser votre portail.
                        </p>
                      </div>
                    </CardBody>
                  </Card>

                  {canCreateOrg && (
                    <div className="text-center pt-4">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handleCreateOrganization}
                        leftIcon={<Plus className="w-5 h-5" />}
                      >
                        {t('organizations.manage.createNew')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            filteredOrganizations.map((org) => (
              <Card
                key={org.id}
                variant="default"
                className="hover:shadow-xl hover:scale-[1.02] transition-all duration-300 border-border bg-surface overflow-hidden group"
              >
                <CardBody className="p-6">
                  {/* Header avec logo et infos */}
                  <div className="flex items-start gap-4 mb-5">
                    <div className="relative flex-shrink-0">
                      {org.organizationInfo?.logo ? (
                        <img
                          src={org.organizationInfo.logo}
                          alt={org.organizationInfo.name}
                          className="w-16 h-16 rounded-2xl object-cover bg-background-secondary border-2 border-border-light flex-shrink-0 shadow-sm"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:shadow-lg transition-shadow">
                          <Building2 className="w-8 h-8 text-white" />
                        </div>
                      )}
                      {/* Badge de rôle */}
                      <div className="absolute -bottom-1 -right-1 bg-surface border-2 border-surface rounded-full p-1">
                        {getRoleIcon(org.role)}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-lg text-foreground truncate mb-1">
                            {org.organizationInfo?.name || org.organizationId}
                          </h3>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge
                              variant="neutral"
                              size="sm"
                              className="text-xs"
                            >
                              {getRoleLabel(org.role)}
                            </Badge>
                            {viewMode === 'all' && isSuperAdmin && (
                              <Badge variant="info" size="sm" className="border-0 bg-gradient-to-r from-secondary/20 to-secondary-light/20 text-secondary-dark">
                                <Crown className="w-2.5 h-2.5" />
                                {t('organizations.manage.adminGlobal')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-foreground-secondary">
                        <span>{t('organizations.manage.lastUpdated')}</span>
                        <span className="font-medium">{formatJoinedDate(org.lastUpdatedAt)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Statut de la configuration - Style amélioré */}
                  {org.configStatus && (
                    <div className={`flex items-center justify-between p-3 rounded-xl text-sm mb-4 ${org.configStatus.isPortalReady
                      ? 'bg-success-10 border border-success-20'
                      : 'bg-warning/10 border border-warning/20'
                      }`}>
                      <div className="flex items-center gap-2.5">
                        <div className={`w-2 h-2 rounded-full ${org.configStatus.isPortalReady ? 'bg-success animate-pulse' : 'bg-warning'
                          }`} />
                        <span className={`font-semibold text-sm ${org.configStatus.isPortalReady ? 'text-success-dark' : 'text-warning-dark'
                          }`}>
                          {org.configStatus.isPortalReady
                            ? t('organizations.manage.configComplete')
                            : t('organizations.manage.configIncomplete')}
                        </span>
                      </div>
                      {!org.configStatus.isPortalReady && (
                        <Badge variant="warning" size="sm" className="text-xs">
                          {getConfigIncompleteLabel(org)}
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Boutons d'action - Présentation différenciée */}
                  <div className="pt-4 border-t border-border-light space-y-2">
                    {/* Bouton principal - Tableau de bord */}
                    <Button
                      variant={viewMode === 'all' && isSuperAdmin ? 'secondary' : 'primary'}
                      size="sm"
                      onClick={() => handleAdminClick(org.organizationId)}
                      leftIcon={<Settings className="w-4 h-4" />}
                      className="w-full justify-center"
                    >
                      {t('organizations.manage.adminButton')}
                    </Button>

                    {/* Bouton secondaire - Portail */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOrganizationClick(org.organizationId)}
                      leftIcon={<Users className="w-4 h-4" />}
                      className="w-full justify-center text-foreground-secondary hover:text-foreground hover:bg-background-tertiary"
                    >
                      {t('organizations.manage.viewPortalButton')}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ManageOrganizationsPage;
